'use client';

import React, { useEffect, useRef, useState } from 'react';

const AnimatedCirclesBackground: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Detect mobile
    const checkMobile = window.innerWidth < 768;
    setIsMobile(checkMobile);
    // On desktop, render immediately
    if (!checkMobile) {
      setShouldRender(true);
    }

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setShouldRender(true);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // On mobile, use Intersection Observer to defer rendering
    if (isMobile && !shouldRender) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            // Defer rendering by 2 seconds on mobile after entering viewport
            setTimeout(() => setShouldRender(true), 2000);
            observer.disconnect();
          }
        },
        { threshold: 0.01, rootMargin: '100px' },
      );

      observer.observe(container);
      return () => observer.disconnect();
    }

    // Track visibility for animation
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.01 },
    );

    visibilityObserver.observe(container);
    return () => visibilityObserver.disconnect();
  }, [isMobile, shouldRender]);

  const areaStyles: React.CSSProperties = {
    background: 'linear-gradient(to bottom, #0f423a, #0e0f0f)',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: -1,
    contain: 'layout style paint',
  };

  const circlesStyles: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  };

  const getCircleStyles = (index: number): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      display: 'block',
      listStyle: 'none',
      width: '20px',
      height: '20px',
      background: 'rgba(255, 255, 255, 0.08)',
      animation: isVisible ? 'circleAnimate 25s linear infinite' : 'none',
      bottom: '-150px',
      willChange: 'transform, opacity',
      backfaceVisibility: 'hidden',
    };

    const circleVariations = [
      { left: '25%', width: '80px', height: '80px', animationDelay: '0s' },
      {
        left: '10%',
        width: '20px',
        height: '20px',
        animationDelay: '2s',
        animationDuration: '12s',
      },
      { left: '70%', width: '20px', height: '20px', animationDelay: '4s' },
      {
        left: '40%',
        width: '60px',
        height: '60px',
        animationDelay: '0s',
        animationDuration: '18s',
      },
      { left: '65%', width: '20px', height: '20px', animationDelay: '0s' },
      { left: '75%', width: '110px', height: '110px', animationDelay: '3s' },
      { left: '35%', width: '150px', height: '150px', animationDelay: '7s' },
      {
        left: '50%',
        width: '25px',
        height: '25px',
        animationDelay: '15s',
        animationDuration: '45s',
      },
      {
        left: '20%',
        width: '15px',
        height: '15px',
        animationDelay: '2s',
        animationDuration: '35s',
      },
      {
        left: '85%',
        width: '150px',
        height: '150px',
        animationDelay: '0s',
        animationDuration: '11s',
      },
    ];

    return {
      ...baseStyles,
      ...circleVariations[index],
    };
  };

  return (
    <div ref={containerRef} style={areaStyles}>
      {shouldRender && (
        <ul style={circlesStyles}>
          {/* Show half circles on mobile (5 instead of 10) */}
          {Array.from({ length: isMobile ? 5 : 10 }, (_, index) => (
            <li key={index} style={getCircleStyles(index)}></li>
          ))}
        </ul>
      )}
      <style jsx>{`
        @keyframes circleAnimate {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
            border-radius: 0;
          }
          100% {
            transform: translateY(-1000px) rotate(720deg);
            opacity: 0;
            border-radius: 50%;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedCirclesBackground;
